import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../core/patterns/icon/icon'
import ClearIcon from '../../resources/svgs/Cross.svg' //New clearIcon

import { FeatureTogglesContext } from '../../core/state/context/featureToggles'

import keyboard from '../../core/services/keyboard/keyboard'

const defaultSelected = -1

//For Old Search
const defaultMaxResults = 5

export default class Search extends PureComponent {
  static contextType = FeatureTogglesContext

  static propTypes = {
    styles: PropTypes.object.isRequired,
    getInputRef: PropTypes.func,
    onFocus: PropTypes.func,
    onClickOutside: PropTypes.func,
    onClick: PropTypes.func,
    onLoaded: PropTypes.func,
    onRequest: PropTypes.func.isRequired,
    onResultSelected: PropTypes.func,
    placeholder: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    resultTemplate: PropTypes.func,
    footerTemplate: PropTypes.func,
    noMatchesText: PropTypes.string,
    showButton: PropTypes.bool,
    buttonText: PropTypes.string,
    buttonIcon: PropTypes.object,
    idSuffix: PropTypes.string,
    value: PropTypes.string,
    action: PropTypes.string,
    maxResults: PropTypes.number,
  }

  constructor(props, context) {
    super(props, context)
    this.inputEl = null
    this.formEl = null
    this.state = {
      results: [],
      shown: false,
      selected: defaultSelected,
      value: props.value || '',
      isLoading: false,
    }
  }

  componentDidMount() {
    if (this.props.onLoaded) {
      this.props.onLoaded(this._onLoaded())
    }

    document.addEventListener('click', this._handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._handleClickOutside)
  }

  _getInputRef = node => {
    this.inputEl = node
    if (this.props.getInputRef) {
      this.props.getInputRef(node)
    }
  }

  _getFormRef = node => (this.formEl = node)

  _onLoaded = () => {
    return {
      setValue: value =>
        this.setState(state => ({ ...state, value, shown: false })),
    }
  }

  _handleClickOutside = event => {
    if (this.formEl && !this.formEl.contains(event.target)) {
      this.setState(state => ({ ...state, shown: false, results: [] }))
      this.props.onClickOutside && this.props.onClickOutside()
    }
  }

  _onChange = event => {
    const { value } = event.target
    let modifiedState

    if (value.length === 0) {
      // when field is empty
      modifiedState = { value, shown: false, results: [] }
    } else if (value.length >= 2) {
      // when we want to start requesting data
      modifiedState = { value, isLoading: true }
      // request data
      this.props.onRequest(value, (results = []) => {
        if (!this.state.isLoading) return false
        this.setState(state => ({
          ...state,
          results,
          shown: true,
          selected: defaultSelected,
          isLoading: false,
        }))
      })
    } else {
      // any other typing
      modifiedState = { value }
    }

    this.setState(state => ({ ...state, ...modifiedState }))
  }

  _onFormSubmit = event => {
    event.preventDefault()

    const { results, selected, value } = this.state
    let inputValue = value.toLowerCase()

    let modifiedState = {
      results: [],
      shown: false,
      isLoading: false,
    }

    this.inputEl.blur()

    const resultSelected = results[selected]

    if (resultSelected) {
      modifiedState = { ...modifiedState, value: resultSelected.name }
      inputValue = resultSelected.name
    }

    this.setState(state => ({
      ...state,
      ...modifiedState,
    }))

    this.props.onResultSelected(resultSelected, inputValue)
  }

  _onKeyUp = ({ keyCode }) => {
    const { selected, shown, results } = this.state
    const { maxResults = defaultMaxResults } = this.props

    const searchResultsLength = this.context.isAdvancedSearchEnabled
      ? results.length
      : maxResults

    if (!shown) return false

    keyboard.onKeyDown(keyCode, {
      onUp: () =>
        this.setState(state => ({
          ...state,
          selected: selected === defaultSelected ? selected : selected - 1,
        })),
      onDown: () =>
        this.setState(state => ({
          ...state,
          selected:
            selected >= searchResultsLength - 1 ? selected : selected + 1,
        })),
    })
  }

  _onInputClear = () => {
    this.setState(state => ({ ...state, results: [], shown: false, value: '' }))
  }

  _renderInputLabel = () => {
    const { label, idSuffix, styles } = this.props

    if (!label) return null

    return (
      <label htmlFor={`searchInput_${idSuffix}`} className={styles['label']}>
        {label}
      </label>
    )
  }

  _renderInputClearButton = () => {
    const { styles } = this.props
    if (!this.state.value || !this.context.isAdvancedSearchEnabled) return null

    return (
      <button
        type="button"
        onClick={this._onInputClear}
        className={styles['inputClearButton']}
      >
        <Icon className={styles['inputClearIcon']} glyph={ClearIcon} />
      </button>
    )
  }

  _renderSearchButton = () => {
    const { showButton, styles, buttonIcon, buttonText } = this.props
    if (!showButton) return null

    return (
      <button className={styles['button']} type="submit">
        {buttonIcon ? (
          <Icon className={styles['buttonIcon']} glyph={buttonIcon} />
        ) : null}
        <span className={styles['buttonText']}>{buttonText}</span>
      </button>
    )
  }

  _renderFooter = () => {
    const { value, shown, results } = this.state
    const { footerTemplate } = this.props

    if (shown && results.length && footerTemplate) {
      return <li>{footerTemplate(value.toLowerCase())}</li>
    }

    return null
  }

  _highlightMatchingText = (value = '', style) => {
    const pattern = new RegExp(this.state.value, 'gi')

    return {
      __html: value.replace(
        pattern,
        match => `<span class="${style}">${match}</span>`
      ),
    }
  }

  render() {
    const {
      styles,
      onFocus = null,
      onClick = null,
      onResultSelected = null,
      placeholder = '',
      maxResults = defaultMaxResults,
      resultTemplate,
      noMatchesText,
      idSuffix,
      action = null,
    } = this.props
    const { isAdvancedSearchEnabled } = this.context
    const { selected, results, shown, value, isLoading } = this.state
    //Old Search uses 5 maxResults:
    const searchResults = isAdvancedSearchEnabled
      ? results
      : results.slice(0, maxResults)

    const listClassName =
      shown && results.length ? styles['list'] : styles['listHidden']

    return (
      <form
        ref={this._getFormRef}
        onSubmit={!action ? this._onFormSubmit : null}
        className={styles['container']}
        action={action}
      >
        <div className={styles['wrapper']}>
          {this._renderInputLabel()}
          <input
            id={`searchInput_${idSuffix}`}
            name="keyword"
            type={isAdvancedSearchEnabled ? 'text' : 'search'}
            autoComplete="off"
            ref={this._getInputRef}
            onChange={this._onChange}
            onKeyUp={this._onKeyUp}
            onFocus={onFocus}
            onClick={onClick}
            placeholder={placeholder}
            value={value}
            className={styles['input']}
            required
          />
          {this._renderInputClearButton()}
          {isLoading ? <div className={styles['loader']} /> : null}
          <ul className={listClassName}>
            {searchResults.map((result, i) => (
              <li
                key={result.id || i}
                className={styles['result']}
                onClick={() => onResultSelected(result)}
              >
                {resultTemplate(
                  { result, selected, index: i },
                  this._highlightMatchingText
                )}
              </li>
            ))}
            {this._renderFooter()}
          </ul>
          {shown && results.length === 0 ? (
            <p className={styles['noMatches']}>
              {noMatchesText.replace('%searchterm%', value)}
            </p>
          ) : null}
        </div>
        {this._renderSearchButton()}
      </form>
    )
  }
}
